/* eslint-disable */
import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { defineMessages, FormattedHTMLMessage, useIntl } from 'react-intl';
import { Panel } from 'primereact/panel';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { AccountContext } from '../../../components/AccountContext';
import ReportActionContainer from './assessmentResult/ReportActionContainer';
import MatchingResultPanel from './assessmentResult/MatchingResultPanel';
import { getJobMatchedResult, getStandardJobMatchedResult } from '../../../services/apps/AssessmentServices';
import { growlErrors } from '../../../utils/NotificationUtil';
import { getStandardJob } from '../../../services/apps/JobProfileService';
import { NotificationContext } from '../../../components/GrowlNotifications';
import JobMatch from './assessmentResult/JobMatch';
import { CUSTOM_JOB_MATCH_TYPE, STANDARD_JOB_MATCH_TYPE } from '../../../shared/Constants';

const messages = defineMessages({
  customJobMatch: {
    id: 'assessment.custom-job-match',
    defaultMessage: 'Custom Job Profile match ',
  },
  standardJobMatch: {
    id: 'assessment.standard-job-match',
    defaultMessage: 'Standard Job Profile match ',
  },
});

const ReportPanel = ({ assessment, respondent, jobProfiles, superMatchId }) => {
  const currentAccount = useContext(AccountContext);
  const growl = useContext(NotificationContext);
  const { locale } = useSelector((state) => state.i18n);
  const { formatMessage } = useIntl();

  const [selectedJobId, setSelectedJobId] = useState('');
  const [selectedJob, setSelectedJob] = useState(null);
  const [jobProfileMatchResults, setJobProfileMatchResults] = useState(null);
  const [isMatchingResultLoaded, setIsMatchingResultLoaded] = useState(true);
  const [displayResult, setDisplayResult] = useState(true);
  const [printerFriendlyReports, setPrinterFriendlyReports] = useState(true);
  const [searching, setIsSearching] = useState(false);
  const [currentLanguage, setCurrentLanguage] = useState(locale);
  const [standardJobProfiles, setStandardJobProfiles] = useState([]);
  const [selectedStandardJob, setSelectedStandardJob] = useState(null);
  const [jobMatchType, setJobMatchType] = useState(CUSTOM_JOB_MATCH_TYPE);

  const fetchStandardJobs = async (query) => {
    setIsSearching(true);
    const input = {
      query,
    };

    try {
      const result = await getStandardJob(input);
      setStandardJobProfiles(result);
    } catch (err) {
      growlErrors(growl, err);
    } finally {
      setIsSearching(false);
    }
  };

  useEffect(() => {
    fetchStandardJobs();
  }, []);

  const onLoadMatchingResult = async (value) => {
    setIsMatchingResultLoaded(false);

    try {
      const matchedResult = await getJobMatchedResult({ ...value, accountId: currentAccount });

      setJobProfileMatchResults(matchedResult.jobProfileMatchResults);
      setDisplayResult(true);
    } catch (error) {
      growlErrors(growl, error);
    } finally {
      setIsMatchingResultLoaded(true);
    }
  };

  const onLoadStandardMatchingResult = async (value) => {
    setIsMatchingResultLoaded(false);

    try {
      const matchedResult = await getStandardJobMatchedResult({ ...value, accountId: currentAccount });

      setJobProfileMatchResults(matchedResult.standardJobProfileMatchResults);
      setDisplayResult(true);
    } catch (error) {
      growlErrors(growl, error);
    } finally {
      setIsMatchingResultLoaded(true);
    }
  };

  const selectJobMatching = (selectedJobIdInput) => {
    if (selectedJobIdInput) {
      const jobProfile = jobProfiles.find((job) => job.id === selectedJobIdInput);

      setSelectedJobId(selectedJobIdInput);
      setDisplayResult(false);
      setSelectedJob(jobProfile);
    }
  };

  const selectStandardJobMatching = (selectedJobIdInput) => {
    if (selectedJobIdInput) {
      const standardJobProfile = standardJobProfiles.find((job) => job.id === selectedJobIdInput);

      setDisplayResult(false);
      setSelectedStandardJob(standardJobProfile);
    }
  };

  const selectPreferredLanguage = (lang) => {
    setCurrentLanguage(lang);
    setDisplayResult(false);
  };

  const searchStandardJob = async (query) => {
    fetchStandardJobs(query);
  };

  const changePrinterFriendly = (event) => {
    setPrinterFriendlyReports(event.checked);
  };

  const getJobProfileIdForReport = () => {
    if (jobMatchType === CUSTOM_JOB_MATCH_TYPE) {
      return selectedJobId;
    }

    if (jobMatchType === STANDARD_JOB_MATCH_TYPE) {
      return selectedStandardJob;
    }

    return null;
  };

  const reportParams = {
    'job-profile-id': getJobProfileIdForReport(),
    assessment: assessment.id,
    locale: currentLanguage,
    superMatchId,
  };

  const jobMatchOptions = [
    {
      label: formatMessage(messages.customJobMatch),
      value: CUSTOM_JOB_MATCH_TYPE,
    },
    {
      label: formatMessage(messages.standardJobMatch),
      value: STANDARD_JOB_MATCH_TYPE,
    },
  ];

  return (
    <div className="p-grid">
      <div className="p-md-6 p-sm-12">
        <Panel>
          <div className="job-match-type">
            <Dropdown
              className="job-match-type-dropdown"
              value={jobMatchType}
              options={jobMatchOptions}
              onChange={(event) => {
                setJobMatchType(event.value);
                setDisplayResult(false);
              }}
            />
          </div>
          <JobMatch
            jobMatchType={jobMatchType}
            currentLanguage={currentLanguage}
            assessmentId={assessment.id}
            onLoadMatchingResult={onLoadMatchingResult}
            onLoadStandardMatchingResult={onLoadStandardMatchingResult}
            searchStandardJob={searchStandardJob}
            searching={searching}
            jobProfiles={jobProfiles}
            standardJobProfiles={standardJobProfiles}
            selectJobMatching={selectJobMatching}
            selectStandardJobMatching={selectStandardJobMatching}
            selectPreferredLanguage={selectPreferredLanguage}
            changePrinterFriendly={changePrinterFriendly}
            printerFriendlyReports={printerFriendlyReports}
            selectedJobId={selectedJobId}
            selectedStandardJob={selectedStandardJob}
          />
          <MatchingResultPanel
            selectedJobId={selectedJobId}
            selectedJob={selectedJob}
            displayResult={displayResult}
            isMatchingResultLoaded={isMatchingResultLoaded}
            selectedStandardJob={selectedStandardJob}
            jobProfile={selectedJob}
            jobProfileMatchResults={jobProfileMatchResults}
            respondent={respondent}
          />
        </Panel>
      </div>
      <div className="p-md-6 p-sm-12">
        <Panel>
          <div className="p-grid">
            <div className="p-col-12">
              <Button
                label={
                  <FormattedHTMLMessage
                    id="tooltip.download_open_window_hint"
                    // eslint-disable-next-line max-len
                    defaultMessage="Reports are opened in a <strong>new window</strong>. Please <strong>enable pop-up</strong> windows for this website."
                  />
                }
                icon="pi pi-info-circle"
                disabled
                className="p-button-warning"
              />
            </div>
            <div className="p-col-12">
              <div className="p-grid p-fluid p-justify-center">
                <div className="p-sm-12 p-md-8">
                  <ReportActionContainer
                    jobMatchType={jobMatchType}
                    reportParams={reportParams}
                    assessmentPrinter={printerFriendlyReports}
                  />
                </div>
              </div>
            </div>
          </div>
        </Panel>
      </div>
    </div>
  );
};

ReportPanel.propTypes = {
  assessment: PropTypes.objectOf(PropTypes.any).isRequired,
  respondent: PropTypes.objectOf(PropTypes.any).isRequired,
};

export default ReportPanel;
